import React from 'react';
import {OperatorLocalisationSettings, OperatorLocalisationSettingsData} from "types/Operator";
import {Form, Formik, FormikHelpers} from "formik";
import FormControls from "components/FormControls";
import Selector from "components/Form/Selector";
import * as locales from 'locale-codes';
import {currencies} from "country-data-list";
import {UnitSystem} from "../../../types/UnitSystem";
import {toTitleCase} from "../../../services/string";

// Types ----

type Props = {
  settings?: OperatorLocalisationSettings | null
  onSubmit: (values: OperatorLocalisationSettingsData, helpers: FormikHelpers<OperatorLocalisationSettingsData>) => any
}

// Helpers ----

const localeOptions = locales
  .all
  .filter(locale => !locale.name.startsWith('Pseudo'))
  .map(locale => ({
    id: locale.tag,
    name: locale.name + (!!locale.location && ` - ${locale.location}`),
  }))

const excludedCurrencyCodes = ['USS', 'USN', 'XAG', 'XAU', 'XPD', 'XPT', 'XBA', 'XBB', 'XBC', 'XBD', 'XTS', 'XXX'];

const currencyOptions = currencies
  .all
  .filter(currency => !currency.name.includes('funds code')
    && !currency.name.includes('complementary currency')
    && !currency.name.includes('Lithuanian litas')
    && !excludedCurrencyCodes.includes(currency.code)
  )
  .map(currency => ({
    id: currency.code,
    name: `${currency.code} - ${currency.name}`
  }))

const lengthUnitOptions = Object.values(UnitSystem).map(unitSystem => ({
  id: unitSystem,
  name: toTitleCase(unitSystem),
}))

// Component ----

function LocalisationForm(props: Props) {
  const initialValues: OperatorLocalisationSettingsData = {
    currency: props.settings?.currency || 'GBP',
    locale: props.settings?.locale || 'en-GB',
    length_units: props.settings?.length_units || UnitSystem.METRIC
  }

  return (
    <Formik initialValues={initialValues} onSubmit={props.onSubmit}>
      {formik => (
        <section aria-labelledby="localisation-heading">
          <Form onKeyDown={event => {
            if (event.ctrlKey && event.key === 'Enter') formik.handleSubmit()
          }}>
            <div className="shadow sm:rounded-md">
              <div className="bg-white py-6 px-4 sm:p-6 sm:rounded-md">
                <div>
                  <h2 id="localisation-heading" className="text-lg leading-6 font-medium text-gray-900">
                    Localisation Settings
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Adjust how content is displayed for the operator location
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-6 gap-6">

                  {/* Locale */}
                  <div className="col-span-6 lg:col-span-2 lg:col-start-1">
                    <Selector
                      label="Locale"
                      items={localeOptions}
                      selectedId={initialValues.locale}
                      onChange={locale => formik.setFieldValue('locale', locale.id)}
                      required
                    />
                  </div>

                  {/* Currency */}
                  <div className="col-span-6 lg:col-span-2 lg:col-start-1">
                    <Selector
                      label="Currency"
                      items={currencyOptions}
                      selectedId={initialValues.currency}
                      onChange={currency => formik.setFieldValue('currency', currency.id)}
                      required
                    />
                  </div>

                  {/* Length Units */}
                  <div className="col-span-6 lg:col-span-2 lg:col-start-1">
                    <Selector
                      label="Length Units"
                      items={lengthUnitOptions}
                      selectedId={initialValues.length_units}
                      onChange={lengthUnit => formik.setFieldValue('length_units', lengthUnit.id)}
                      required
                    />
                  </div>
                </div>
              </div>

              <FormControls formik={formik} compact withoutCancel/>
            </div>
          </Form>
        </section>
      )}
    </Formik>
  );
}

export default LocalisationForm;